import { contentTypeCreateSchema, contentTypeUpdateSchema } from '@today/models'
import type { ContentTypeDTO, Role } from '@today/types'
import { schemaErrors } from './schema-check'

/** The content-type form (TO-420 content-admin-ui REQ-009). */

export interface TypeFormValues {
	code: string
	name: string
	description: string
	requiresApproval: boolean
	approverRoles: Role[]
	forbidSelfApproval: boolean
	requiredLocales: string[]
	allowLocaleFallback: boolean
}

export const EMPTY_TYPE_FORM: TypeFormValues = {
	code: '',
	name: '',
	description: '',
	requiresApproval: true,
	approverRoles: [],
	forbidSelfApproval: true,
	requiredLocales: [],
	allowLocaleFallback: false,
}

const rules = (values: TypeFormValues) => ({
	name: values.name,
	description: values.description.trim() || null,
	requires_approval: values.requiresApproval,
	approver_roles: values.approverRoles,
	forbid_self_approval: values.forbidSelfApproval,
	required_locales: values.requiredLocales,
	allow_locale_fallback: values.allowLocaleFallback,
})

/** `POST /texts/types` body. */
export const typeCreateBody = (values: TypeFormValues): Record<string, unknown> => ({
	code: values.code.trim(),
	...rules(values),
})

/** `PATCH /texts/types/{id}` body: every rule, never `code`. */
export const typeUpdateBody = (values: TypeFormValues): Record<string, unknown> => rules(values)

/**
 * Field → message key; `{}` means the body may be sent. The update schema has
 * no approver refinement, so "approval needs an approver role" is checked here
 * for both modes.
 */
export const typeErrors = (body: Record<string, unknown>, mode: 'create' | 'update'): Record<string, string> => {
	const schema = mode === 'create' ? contentTypeCreateSchema : contentTypeUpdateSchema
	const errors = schemaErrors(schema, body, 'approverRoles')
	// Type codes are lowercase, unlike text codes: use the type-code message.
	if (errors.code) errors.code = 'validation.typeCodeFormat'
	const roles = body.approver_roles
	if (body.requires_approval === true && Array.isArray(roles) && roles.length === 0) {
		errors.approverRoles = 'validation.approverRoles'
	}
	return errors
}

/** The form values of an existing type. */
export const typeToForm = (
	type: Pick<
		ContentTypeDTO,
		| 'code'
		| 'name'
		| 'description'
		| 'requiresApproval'
		| 'approverRoles'
		| 'forbidSelfApproval'
		| 'requiredLocales'
		| 'allowLocaleFallback'
	>,
): TypeFormValues => ({
	code: type.code,
	name: type.name,
	description: type.description ?? '',
	requiresApproval: type.requiresApproval,
	approverRoles: [...type.approverRoles],
	forbidSelfApproval: type.forbidSelfApproval,
	requiredLocales: [...type.requiredLocales],
	allowLocaleFallback: type.allowLocaleFallback,
})
